import { createContext, useContext, useMemo } from 'react';
import AutosaveAlertsContainer from '../components/AutosaveAlertsContainer';
import AutosaveIndicator from '../components/AutosaveIndicator';
import UnsavedChangesAlert from '../components/UnsavedChangesAlert';
import { useAutosaveCore } from '../hooks/useAutosaveCore';
import { useAutosaveEditingState } from '../hooks/useAutosaveEditingState';
import { useSavingMode } from '../hooks/useSavingMode';
import ManualSaveAlert from '../components/ManualSaveAlert';
import { gate } from 'hub-http/gates';
import { useGetIsUngated } from '../../v2/hooks/useGetIsUngated';
import { useAutosaveCoreV2 } from '../hooks/useAutosaveCoreV2';
import { useAutosaveStrategyMap } from '../hooks/useAutosaveStrategyMap';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const AutosaveContext = /*#__PURE__*/createContext(undefined);
export const AutosaveProvider = ({
  children
}) => {
  const parentContext = useContext(AutosaveContext);
  const getIsUngated = useGetIsUngated();
  const isUngatedToAutosaveV2 = getIsUngated(gate('CRM:Properties:Autosave:V2'));
  const {
    data: savingMode
  } = useSavingMode();
  const {
    getIsEditing,
    setIsEditing
  } = useAutosaveEditingState();
  const strategyMap = useAutosaveStrategyMap();
  const {
    cancelRequestsForSource,
    clearPendingRequests,
    enqueueRequest,
    hasRequestForSource,
    invalidRequests,
    lastSavedItems,
    saveValidRequests,
    registerHandler,
    requests,
    status,
    undo
  } = isUngatedToAutosaveV2 ?
  // gates won't change over the lifetime of the app
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useAutosaveCoreV2({
    strategies: strategyMap
  }) :
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useAutosaveCore();
  const value = useMemo(() => {
    return {
      getIsEditing,
      setIsEditing,
      cancelRequestsForSource,
      enqueueRequest,
      hasRequestForSource,
      registerHandler,
      saveValidRequests
    };
  }, [cancelRequestsForSource, enqueueRequest, getIsEditing, hasRequestForSource, registerHandler, saveValidRequests, setIsEditing]);
  if (parentContext !== undefined) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }
  return /*#__PURE__*/_jsxs(AutosaveContext.Provider, {
    value: value,
    children: [/*#__PURE__*/_jsxs(AutosaveAlertsContainer, {
      children: [savingMode === 'manual' ? /*#__PURE__*/_jsx(ManualSaveAlert, {
        invalidRequests: invalidRequests,
        onSave: saveValidRequests,
        onCancel: clearPendingRequests,
        requests: requests
      }) : /*#__PURE__*/_jsx(UnsavedChangesAlert, {
        requests: requests
      }), /*#__PURE__*/_jsx(AutosaveIndicator, {
        status: status,
        onUndo: undo,
        items: lastSavedItems
      })]
    }), children]
  });
};
export const useAutosaveContext = () => {
  const context = useContext(AutosaveContext);
  if (context === undefined) {
    throw new Error('useAutosaveContext must be used within AutosaveProvider');
  }
  return context;
};